import React, { ReactElement } from 'react';

// MUI
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// OL
import { Geometry } from 'ol/geom';
import { Feature } from 'ol';

// Custom Components
import { GridContainer, GridItem } from '@/ui/Grid';
import FormGenerator from '@/lib/FormGenerator';
import FeatureMap from '@/views/GIS/FeatureMap';
import FormSectionTitle from '@/components/FormSectionTitle';

// Types
import { ControlField, FieldAny, IFieldsCollection, IFieldsWControlCollection, IFormLayout } from '@/@types/models/model';
import { ChangeHandlerFunction, FormFieldControls } from '@/@types/lib/formGenerator';
import { PickerDataControllers } from '@/@types/components/authFormController';
import { RecordValidation } from '@/@types/lib/appValidator';
import { DCRecord } from '@/@types/lib/dataController';

export type FormContentMode = 'view' | 'insert' | 'update' | 'simpleView';
export type FormContentColumns = 1 | 2 | 3 | 4;
export type PickerStyleProps = {
  ListboxMaxHeight?: number;
};

export type FormContentProps = {
  title?: string;
  pickerDataControllers?: PickerDataControllers;
  mode: FormContentMode;
  onFieldChange?: ChangeHandlerFunction;
  onSubmit?: () => void;
  fieldNames: Array<string>;
  validation?: RecordValidation;
  fields: IFieldsCollection;
  layout?: IFormLayout;
  columns?: FormContentColumns;
  record: DCRecord;
  hidden?: string[];
  pickerStyleProps?: PickerStyleProps;
  geometryChangeType?: 'LineString' | 'Polygon' | 'Point';
  mapId?: number;
  mapHeight?: number | string;
  collapsibleSections?: boolean;
};

const FormContent = (props: FormContentProps) => {
  const { onFieldChange, onSubmit, columns } = props;
  const {
    title,
    mode,
    record,
    fieldNames,
    validation,
    pickerDataControllers,
    fields,
    layout,
    hidden,
    pickerStyleProps,
    geometryChangeType,
    mapId,
    mapHeight,
    collapsibleSections = false,
  } = props;

  const fg = new FormGenerator();

  type HashMap = { [id: string | number]: any };

  const handleFieldChange = (value: unknown, source: string) => {
    let isValueParsedAndSubmit = false;
    try {
      const tmp: HashMap = JSON.parse(value as string);
      isValueParsedAndSubmit = '#submit' in tmp && tmp['#submit'];
    } catch (_) {
      // do nothing and proceed
    }

    if (
      isValueParsedAndSubmit /* value && typeof value == "object" && value.hasOwnProperty("keyCode") && (value as { keyCode: number })["keyCode"] === 13 */
    ) {
      if (onSubmit) {
        onSubmit();
      }
    } else if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleGeometryChange = (geom?: Feature<Geometry>) => {
    handleFieldChange(geom, 'geom');
    handleFieldChange(true, 'is_geom_changed');
  };

  const getWidth = (f: FieldAny, cols?: FormContentColumns): number => {
    if (f && f.width && f.width === 'full') {
      return 12;
    }
    switch (cols) {
      case 1:
        return 12;
      case 2:
        return 6;
      case 3:
        return 4;
      case 4:
        return 3;
      default:
        return 6;
    }
  };

  const renderFields: IFieldsWControlCollection = fieldNames
    ? fieldNames
        .map((name) => fields.find((f) => f.source === name))
        .filter((x) => x !== undefined)
        .map((x) => x as ControlField)
    : [];

  let controls: FormFieldControls = {};
  switch (mode) {
    case 'insert':
    case 'update': {
      controls = fg.generateFormControls(
        renderFields,
        'form',
        'edit',
        record,
        validation,
        handleFieldChange,
        pickerDataControllers,
        pickerStyleProps
      );
      break;
    }
    case 'view': {
      controls = fg.generateFormControls(
        renderFields,
        'form',
        'view',
        record,
        undefined,
        undefined,
        pickerDataControllers,
        pickerStyleProps
      );
      break;
    }
    case 'simpleView': {
      controls = fg.generateFormControls(
        renderFields,
        'form',
        'simpleView',
        record,
        undefined,
        undefined,
        pickerDataControllers,
        pickerStyleProps
      );
      break;
    }
    default:
      break;
  }

  renderFields.forEach((field) => {
    // eslint-disable-next-line no-param-reassign
    field.control = Object.prototype.hasOwnProperty.call(controls, field.source) ? (controls[field.source] as ReactElement) : <>-</>;
  });

  const defaultLayout: IFormLayout = [
    {
      title,
      columns: columns || 2,
      fieldNames,
    },
  ];

  const hasCustomLayout = Array.isArray(layout) && layout.length > 0;

  const drawLayout = hasCustomLayout ? layout : defaultLayout;

  const renderSectionFields = (section: IFormLayout[number], si: number) =>
    section.fieldNames.map((sf) => {
      const f = renderFields.find((x) => x.source === sf);
      const gridItemId = `grid-${si}-${sf}`;
      return f && hidden?.includes(f.source) ? (
        <Box key={gridItemId} display="none">
          {f.control}
        </Box>
      ) : f?.type === 'wkt' ? (
        <GridItem size={{ xs: 12 }} key={gridItemId} sx={{ height: mapHeight || 400 }}>
          {(mode === 'view' || !geometryChangeType) && (
            <FeatureMap
              key={`${gridItemId}-feature-map-view`}
              field={f}
              feature={record?.geom as Feature<Geometry> | undefined}
              mode="view"
              mapId={mapId}
            />
          )}
          {mode === 'insert' && geometryChangeType && (
            <FeatureMap
              key={`${gridItemId}-feature-map-insert-${geometryChangeType}`}
              field={f}
              mode="insert"
              mapId={mapId}
              geometryChangeType={geometryChangeType}
              onGeometryChange={handleGeometryChange}
            />
          )}
          {mode === 'update' && geometryChangeType && (
            <FeatureMap
              key={`${gridItemId}-feature-map-update-${geometryChangeType}`}
              field={f}
              feature={record?.geom as Feature<Geometry> | undefined}
              mode="update"
              mapId={mapId}
              geometryChangeType={geometryChangeType}
              onGeometryChange={handleGeometryChange}
            />
          )}
        </GridItem>
      ) : f ? (
        <GridItem size={{ xs: 12, sm: getWidth(f, section.columns) }} key={gridItemId}>
          {f.control}
        </GridItem>
      ) : (
        <GridItem size={12} key={gridItemId}>
          <Skeleton variant="rectangular" height={60} />
        </GridItem>
      );
    });

  const useAccordion = collapsibleSections && drawLayout.some((s) => s.title);

  let titledSectionIndex = 0;

  return (
    <>
      {drawLayout.map((section, si) => {
        if (useAccordion && section.title) {
          const isFirstTitled = titledSectionIndex === 0;
          titledSectionIndex += 1;
          return (
            <GridItem size={12} key={`section-${section.title}`} sx={{ px: 0 }}>
              <Accordion
                defaultExpanded={isFirstTitled}
                disableGutters
                elevation={0}
                sx={{
                  background: 'transparent',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 0,
                    p: 0,
                    '& .MuiAccordionSummary-content': { m: 0 },
                    '& .MuiAccordionSummary-expandIconWrapper': { ml: 0.5 },
                  }}
                >
                  <Typography variant="h6" fontSize={mode === 'simpleView' ? '1rem' : undefined}>
                    {section.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, pt: 0.5 }}>
                  <GridContainer spacing={0}>{renderSectionFields(section, si)}</GridContainer>
                </AccordionDetails>
              </Accordion>
            </GridItem>
          );
        }

        return (
          <React.Fragment key={`section-flat-${section.fieldNames[0] ?? si}`}>
            <FormSectionTitle titleToken={section.title} fontSize={mode === 'simpleView' ? '1rem' : undefined} />
            {renderSectionFields(section, si)}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default FormContent;
